import * as XLSX from 'xlsx'
import type { AdmCategoria, AdmCentroCusto } from '../types/admCadastros'

/**
 * Cabeçalhos esperados na linha informada em `headerRow` (ordem de colunas livre):
 * TIPO PGTO | C.CUSTOS | CATEGORIA | DATA | DESCRIÇÃO | PARCELA | VALOR PAGO | VALOR A PAGAR | OBS NOTAS
 *
 * Os dados começam na linha `firstDataRow` (deve ser maior que `headerRow`).
 * O fim da área percorrida é a última linha em que alguma das colunas reconhecidas no cabeçalho
 * (TIPO PGTO, DATA, etc.) está preenchida — assim não depende da coluna A e ignora o “rabo” vazio da folha.
 */
export type ParsedPlanilhaLinha = {
  payment_type: string | null
  cost_center_id: string | null
  category_id: string | null
  expense_date: string | null
  description: string | null
  installment: string | null
  amount_paid: number | null
  amount_to_pay: number | null
  notes: string | null
}

export type ParsePlanilhaPagamentosResult =
  | { ok: true; rows: ParsedPlanilhaLinha[] }
  | { ok: false; message: string }

/** Opções de leitura da planilha (numeracão de linhas como no Excel: 1 = primeira linha). */
export type ParsePlanilhaPagamentosOptions = {
  /** Nome da aba (worksheet) a ler. Se omitido, usa a primeira aba do arquivo. */
  sheetName?: string
  /** Linha do Excel onde estão os títulos das colunas. Padrão: 1. */
  headerRow?: number
  /** Linha do Excel onde começa o primeiro registro de dados. Padrão: 2. */
  firstDataRow?: number
}

/** Lista os nomes das abas de um arquivo .xlsx / .xls (ordem do Excel). */
export function getExcelSheetNamesFromBuffer(
  arrayBuffer: ArrayBuffer
): { ok: true; names: string[] } | { ok: false; message: string } {
  try {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' })
    const names = workbook.SheetNames ?? []
    if (!names.length) {
      return { ok: false, message: 'O arquivo não contém nenhuma aba.' }
    }
    return { ok: true, names }
  } catch {
    return { ok: false, message: 'Não foi possível ler a lista de abas do arquivo.' }
  }
}

function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '')
}

/** Espaços “estranhos” do Excel (NBSP, narrow NBSP, etc.) viram espaço normal antes de colapsar. */
function normalizeUnicodeSpaces(s: string): string {
  return s
    .replace(/\u00A0|\u1680|\u2000|\u2001|\u2002|\u2003|\u2004|\u2005|\u2006|\u2007|\u2008|\u2009|\u200A|\u202F|\u205F|\u3000/g, ' ')
    .replace(/\u200B|\u200C|\u200D|\uFEFF/g, '')
}

function normalizeHeaderKey(raw: unknown): string {
  let s = String(raw ?? '')
    .replace(/^\uFEFF/, '')
  s = normalizeUnicodeSpaces(s)
    .trim()
    .replace(/^[\s"'“”‘’`]+|[\s"'“”‘’`]+$/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\s+/g, ' ')
  try {
    s = s.normalize('NFKC')
  } catch {
    /* ignore */
  }
  s = stripDiacritics(s).toLowerCase()
  /** Remove sufixos comuns de título (nota de rodapé, unidade) para bater sinônimos e includes. */
  s = s.replace(/\s*[*#:•]+\s*$/, '').replace(/\s*\([^)]*\)\s*$/, '').trim()
  return s
}

type FieldKey =
  | 'payment_type'
  | 'cost_center'
  | 'category'
  | 'expense_date'
  | 'description'
  | 'installment'
  | 'amount_paid'
  | 'amount_to_pay'
  /** Coluna só "VALOR" / "VLR" (sem "pago" ou "a pagar" no título). */
  | 'amount_valor'
  | 'notes'

const HEADER_SYNONYMS: Record<string, FieldKey> = {
  // TIPO PGTO
  'tipo pgto': 'payment_type',
  tipopgto: 'payment_type',
  'tipo pagamento': 'payment_type',
  // C.CUSTOS
  'c.custos': 'cost_center',
  'c. custos': 'cost_center',
  'c custos': 'cost_center',
  ccustos: 'cost_center',
  'centro de custo': 'cost_center',
  'centro custo': 'cost_center',
  // CATEGORIA
  categoria: 'category',
  // DATA
  data: 'expense_date',
  // DESCRIÇÃO
  descricao: 'description',
  // PARCELA
  parcela: 'installment',
  // VALOR (genérico — sem pago/a pagar no cabeçalho)
  valor: 'amount_valor',
  vlr: 'amount_valor',
  'valor total': 'amount_valor',
  'valor r$': 'amount_valor',
  'valor rs': 'amount_valor',
  // VALOR PAGO
  'valor pago': 'amount_paid',
  valorpago: 'amount_paid',
  // VALOR A PAGAR (variantes com espaço / sem espaço entre “a” e “pagar”)
  'valor a pagar': 'amount_to_pay',
  valoraapagar: 'amount_to_pay',
  'valor apagar': 'amount_to_pay',
  'vlr a pagar': 'amount_to_pay',
  'vlr. a pagar': 'amount_to_pay',
  vlraapagar: 'amount_to_pay',
  'vlr apagar': 'amount_to_pay',
  // OBS NOTAS
  'obs notas': 'notes',
  'obs / notas': 'notes',
  obs: 'notes',
  notas: 'notes',
}

function mapHeaderToField(normalized: string): FieldKey | null {
  if (normalized in HEADER_SYNONYMS) return HEADER_SYNONYMS[normalized as keyof typeof HEADER_SYNONYMS]
  /** "valor a pagar" antes de "valor pago": evita cabeçalhos compostos ou caracteres raros priorizarem o match errado. */
  if (normalized.includes('valor a pagar') || normalized.includes('vlr a pagar') || normalized.includes('vlr. a pagar')) {
    return 'amount_to_pay'
  }
  if (normalized.includes('valor apagar') || normalized.includes('vlr apagar')) return 'amount_to_pay'
  if (normalized.includes('valor pago') || normalized.includes('vlr pago') || normalized.includes('vlr. pago')) {
    return 'amount_paid'
  }
  if (normalized.includes('tipo') && normalized.includes('pgto')) return 'payment_type'
  if (normalized.includes('centro') && normalized.includes('custo')) return 'cost_center'
  if (normalized.includes('c.custos') || normalized.includes('c. custos') || normalized.includes('c custos')) return 'cost_center'
  if (normalized.includes('categoria')) return 'category'
  if (normalized.includes('descricao')) return 'description'
  if (normalized.includes('parcela')) return 'installment'
  if (normalized.includes('obs') && normalized.includes('notas')) return 'notes'
  if (normalized === 'obs' || normalized === 'notas') return 'notes'
  if (normalized === 'data') return 'expense_date'
  return null
}

function buildColumnMap(headerRow: unknown[]): Map<FieldKey, number> {
  const map = new Map<FieldKey, number>()
  headerRow.forEach((cell, colIdx) => {
    const key = normalizeHeaderKey(cell)
    if (!key) return
    const field = mapHeaderToField(key)
    if (field && !map.has(field)) map.set(field, colIdx)
  })
  return map
}

function cellString(row: unknown[], col: number | undefined): string {
  if (col === undefined) return ''
  const v = row[col]
  if (v === null || v === undefined) return ''
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10)
  return String(v).trim()
}

function excelSerialToDateString(serial: number): string | null {
  if (typeof serial !== 'number' || Number.isNaN(serial)) return null
  const utc = (serial - 25569) * 86400 * 1000
  const d = new Date(utc)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

function parseDateCell(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10)
  if (typeof v === 'number') return excelSerialToDateString(v)
  const s = String(v).trim()
  if (!s) return null
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(s)
  if (iso) return iso[0]
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s)
  if (br) {
    const dd = br[1].padStart(2, '0')
    const mm = br[2].padStart(2, '0')
    const yyyy = br[3]
    return `${yyyy}-${mm}-${dd}`
  }
  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return null
}

function parseMoneyCell(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  let s = String(v).trim().replace(/\s/g, '').replace(/R\$\s?/i, '')
  if (!s) return null
  s = s.replace(/[^\d,.-]/g, '')
  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      s = s.replace(/\./g, '').replace(',', '.')
    } else {
      s = s.replace(/,/g, '')
    }
  } else if (lastComma > -1) {
    s = s.replace(',', '.')
  }
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function normalizeCadastroName(s: string): string {
  return stripDiacritics(s.trim().toLowerCase()).replace(/\s+/g, ' ')
}

function resolveIdByName(
  name: string | null,
  list: Array<{ id: string; name: string }>
): string | null {
  if (!name || !name.trim()) return null
  const n = normalizeCadastroName(name)
  const found = list.find((x) => normalizeCadastroName(x.name) === n)
  return found?.id ?? null
}

function rowIsEmpty(row: unknown[]): boolean {
  return row.every((c) => c === null || c === undefined || String(c).trim() === '')
}

function cellHasDisplayValue(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (v instanceof Date && !Number.isNaN(v.getTime())) return true
  if (typeof v === 'number' && !Number.isNaN(v)) return true
  return String(v).trim() !== ''
}

/** Alguma das colunas mapeadas no cabeçalho tem valor (texto, número ou data). */
function rowHasAnyMappedColumnNonBlank(row: unknown[] | undefined, colMap: Map<FieldKey, number>): boolean {
  if (!row) return false
  const mappedCols = Array.from(colMap.values())
  for (let i = 0; i < mappedCols.length; i++) {
    const col = mappedCols[i]
    if (col === undefined) continue
    const v = row[col]
    if (cellHasDisplayValue(v)) return true
  }
  return false
}

/** Último índice 0-based a partir de `fromIdx` onde a linha tem conteúdo em alguma coluna mapeada. */
function lastRowIndexWithAnyMappedColumnFilled(
  matrix: unknown[][],
  fromIdx: number,
  colMap: Map<FieldKey, number>
): number {
  let last = -1
  for (let r = fromIdx; r < matrix.length; r++) {
    const row = matrix[r] as unknown[]
    if (rowHasAnyMappedColumnNonBlank(row, colMap)) last = r
  }
  return last
}

export function parsePlanilhaPagamentosFile(
  arrayBuffer: ArrayBuffer,
  categorias: AdmCategoria[],
  centrosCusto: AdmCentroCusto[],
  options?: ParsePlanilhaPagamentosOptions
): ParsePlanilhaPagamentosResult {
  let workbook: XLSX.WorkBook
  try {
    workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true })
  } catch {
    return { ok: false, message: 'Não foi possível ler o arquivo Excel.' }
  }
  const available = workbook.SheetNames ?? []
  if (!available.length) return { ok: false, message: 'A planilha não contém abas.' }
  const requested = options?.sheetName?.trim()
  let sheetName: string
  if (requested) {
    if (!available.includes(requested)) {
      return {
        ok: false,
        message: `A aba "${requested}" não existe neste arquivo. Abas disponíveis: ${available.join(', ')}.`,
      }
    }
    sheetName = requested
  } else {
    sheetName = available[0]
  }
  const sheet = workbook.Sheets[sheetName]
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: true }) as unknown[][]
  if (!matrix.length) return { ok: false, message: 'A planilha está vazia.' }

  const hdrOpt = options?.headerRow
  const headerLine = Number.isFinite(hdrOpt as number) ? Math.max(1, Math.floor(hdrOpt as number)) : 1
  const dataOpt = options?.firstDataRow
  const dataLine = Number.isFinite(dataOpt as number) ? Math.max(1, Math.floor(dataOpt as number)) : 2

  if (dataLine <= headerLine) {
    return {
      ok: false,
      message:
        'A linha do primeiro registro deve ser maior que a linha do cabeçalho (ex.: cabeçalho 4 e primeiro registro 5).',
    }
  }

  const headerRowIndex = headerLine - 1
  const dataStartIndex = dataLine - 1

  if (matrix.length <= headerRowIndex) {
    return {
      ok: false,
      message: `A planilha não contém a linha de cabeçalho (linha ${headerLine} do Excel). Ajuste os números informados.`,
    }
  }

  if (dataStartIndex >= matrix.length) {
    return {
      ok: false,
      message: `A planilha não contém a linha do primeiro registro (linha ${dataLine} do Excel).`,
    }
  }

  const headerRowCells = matrix[headerRowIndex] as unknown[]
  const colMap = buildColumnMap(headerRowCells)
  if (!colMap.has('expense_date') && !colMap.has('description')) {
    return {
      ok: false,
      message:
        `Não foi possível identificar colunas (DATA e/ou DESCRIÇÃO) na linha ${headerLine} (cabeçalho). Verifique a linha do cabeçalho e os títulos das colunas.`,
    }
  }

  const cats = categorias.filter((c) => !c.deleted_at)
  const centros = centrosCusto.filter((c) => !c.deleted_at)

  const lastDataRowIndex = lastRowIndexWithAnyMappedColumnFilled(matrix, dataStartIndex, colMap)
  if (lastDataRowIndex < dataStartIndex) {
    return {
      ok: false,
      message:
        'Não há linhas com dados nas colunas reconhecidas a partir da linha do primeiro registro. Verifique as linhas do cabeçalho e do primeiro registro.',
    }
  }

  const fixedRows: ParsedPlanilhaLinha[] = []
  for (let r = dataStartIndex; r <= lastDataRowIndex; r++) {
    const row = matrix[r] as unknown[]
    if (!row || rowIsEmpty(row)) continue
    if (!rowHasAnyMappedColumnNonBlank(row, colMap)) continue

    const get = (f: FieldKey) => cellString(row, colMap.get(f))

    const costRaw = get('cost_center') || null
    const catRaw = get('category') || null
    const costCenterId = resolveIdByName(costRaw, centros)
    const categoryId = resolveIdByName(catRaw, cats)

    const dateCol = colMap.get('expense_date')
    const dateRaw = dateCol !== undefined ? row[dateCol] : ''
    const expense_date = parseDateCell(dateRaw)

    const description = get('description') || null

    const apIdx = colMap.get('amount_paid')
    const atpIdx = colMap.get('amount_to_pay')
    const avIdx = colMap.get('amount_valor')
    const gv = avIdx !== undefined ? parseMoneyCell(row[avIdx]) : null
    let amount_paid = apIdx !== undefined ? parseMoneyCell(row[apIdx]) : null
    let amount_to_pay = atpIdx !== undefined ? parseMoneyCell(row[atpIdx]) : null
    /** Só repete VALOR genérico em colunas que não existem no cabeçalho (evita “pagar” em branco receber outro valor). */
    if (gv !== null) {
      if (amount_paid === null && apIdx === undefined) amount_paid = gv
      if (amount_to_pay === null && atpIdx === undefined) amount_to_pay = gv
    }
    fixedRows.push({
      payment_type: get('payment_type') || null,
      cost_center_id: costCenterId,
      category_id: categoryId,
      expense_date,
      description,
      installment: get('installment') || null,
      amount_paid,
      amount_to_pay,
      notes: get('notes') || null,
    })
  }

  if (fixedRows.length === 0) {
    return { ok: false, message: 'Nenhuma linha de dados encontrada após o cabeçalho.' }
  }

  return { ok: true, rows: fixedRows }
}
